import * as angular from "angular";
import {ITimeoutService} from "angular";
import * as _ from "lodash";
import {TerraDraw, TerraDrawPolygonMode, TerraDrawRectangleMode, TerraDrawRenderMode} from "terra-draw";
import {TerraDrawGoogleMapsAdapter} from "terra-draw-google-maps-adapter";

interface MapSelectionInteractionState {
  touchStartListener?: (event: TouchEvent) => void;
  touchMoveListener?: (event: TouchEvent) => void;
  touchEndListener?: (event: TouchEvent) => void;
  wheelListener?: (event: WheelEvent) => void;
  element?: HTMLElement;
  pinchDistance?: number;
}

// Pinch distance change required to step the zoom by one level.
const PINCH_ZOOM_STEP_RATIO = 1.25;

function getPinchDistance(event: TouchEvent): number {
  const dx = event.touches[0].clientX - event.touches[1].clientX;
  const dy = event.touches[0].clientY - event.touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function setMapSelectionInteractionHandlers(mapInstance: google.maps.Map, state: MapSelectionInteractionState, enabled: boolean) {
  if (!mapInstance) {
    return;
  }

  const mapDiv = mapInstance.getDiv();
  if (enabled) {
    mapInstance.setOptions({scrollwheel: true});
    if (!state.touchStartListener) {
      state.touchStartListener = (event: TouchEvent) => {
        state.pinchDistance = event.touches.length >= 2 ? getPinchDistance(event) : undefined;
      };
      mapDiv.addEventListener('touchstart', state.touchStartListener, {passive: true});
    }
    if (!state.touchMoveListener) {
      // Rectangle/polygon modes set the map non-draggable (gestureHandling 'none'),
      // which also kills native pinch zoom, so pinch is re-implemented here the
      // same way the wheel listener below re-implements scroll zoom.
      state.touchMoveListener = (event: TouchEvent) => {
        event.preventDefault();
        if (event.touches.length < 2) {
          return;
        }
        if (!state.pinchDistance) {
          state.pinchDistance = getPinchDistance(event);
          return;
        }
        const zoom = mapInstance.getZoom();
        if (_.isUndefined(zoom)) {
          return;
        }
        const distance = getPinchDistance(event);
        if (distance > state.pinchDistance * PINCH_ZOOM_STEP_RATIO) {
          mapInstance.setZoom(zoom + 1);
          state.pinchDistance = distance;
        } else if (distance < state.pinchDistance / PINCH_ZOOM_STEP_RATIO) {
          mapInstance.setZoom(zoom - 1);
          state.pinchDistance = distance;
        }
      };
      mapDiv.addEventListener('touchmove', state.touchMoveListener, {passive: false});
    }
    if (!state.touchEndListener) {
      state.touchEndListener = (event: TouchEvent) => {
        state.pinchDistance = event.touches.length >= 2 ? getPinchDistance(event) : undefined;
      };
      mapDiv.addEventListener('touchend', state.touchEndListener, {passive: true});
      mapDiv.addEventListener('touchcancel', state.touchEndListener, {passive: true});
    }
    if (!state.wheelListener) {
      state.wheelListener = (event: WheelEvent) => {
        const zoom = mapInstance.getZoom();
        if (_.isUndefined(zoom)) {
          return;
        }
        event.preventDefault();
        mapInstance.setZoom(event.deltaY < 0 ? zoom + 1 : zoom - 1);
      };
      mapDiv.addEventListener('wheel', state.wheelListener, {passive: false});
    }
    state.element = mapDiv;
    return;
  }

  if (state.element) {
    if (state.touchStartListener) {
      state.element.removeEventListener('touchstart', state.touchStartListener);
    }
    if (state.touchMoveListener) {
      state.element.removeEventListener('touchmove', state.touchMoveListener);
    }
    if (state.touchEndListener) {
      state.element.removeEventListener('touchend', state.touchEndListener);
      state.element.removeEventListener('touchcancel', state.touchEndListener);
    }
    if (state.wheelListener) {
      state.element.removeEventListener('wheel', state.wheelListener);
    }
  }
  state.touchStartListener = undefined;
  state.touchMoveListener = undefined;
  state.touchEndListener = undefined;
  state.wheelListener = undefined;
  state.element = undefined;
  state.pinchDistance = undefined;
}

export interface MapPolygonDrawSessionConfig {
  $timeout: ITimeoutService;
  onDrawFinish: (coordinates: number[][]) => void;
  onDrawDelete?: () => void;
}

export interface MapSelectionDrawSessionConfig {
  $timeout: ITimeoutService;
  polygonOptions: google.maps.PolygonOptions;
  onRectangleComplete: (bounds: google.maps.LatLngBounds) => void;
  onPolygonComplete: (polygon: google.maps.Polygon) => void;
  onDeleteCustomPolygons: () => void;
}

export class MapPolygonDrawSession {
  showDrawToolbar: boolean = false;
  showDeleteTool: boolean = false;
  showDrawCompleteTool: boolean = false;
  drawToolMode: string;

  private mapInstance: google.maps.Map;
  private terraDraw: TerraDraw;
  private terraDrawReady: boolean;
  private isDrawingToolsEnabled: boolean;
  private pendingDrawingToolsEnabled: boolean;
  private terraDrawFinishHandler: (id: string | number) => void;
  private terraDrawChangeHandler: (ids: (string | number)[], type: string) => void;
  private terraDrawContextMenuListener: (event: Event) => void;
  private terraDrawContextMenuElement: HTMLElement;

  constructor(private mapPolygonDrawService: MapPolygonDrawService, private config: MapPolygonDrawSessionConfig) {
    this.terraDrawFinishHandler = (id) => {
      this._handleTerraDrawFinish(id);
    };
    this.terraDrawChangeHandler = () => {
      this._syncDrawCompleteToolVisibility();
    };
  }

  setMapInstance(mapInstance: google.maps.Map) {
    this.mapInstance = mapInstance;
    if (this.pendingDrawingToolsEnabled || this.showDrawToolbar) {
      this._ensureTerraDraw();
    }
  }

  destroy() {
    this.stopDrawing();
  }

  startDrawing() {
    this.showDrawToolbar = true;
    this.isDrawingToolsEnabled = true;
    this.drawToolMode = 'polygon';
    this.showDrawCompleteTool = false;
    this.showDeleteTool = false;

    if (!this.terraDrawReady || !this.terraDraw) {
      this.pendingDrawingToolsEnabled = true;
      this._ensureTerraDraw();
      return;
    }

    this.pendingDrawingToolsEnabled = false;
    this._activatePolygonDrawMode();
  }

  stopDrawing() {
    this.showDrawToolbar = false;
    this.isDrawingToolsEnabled = false;
    this.drawToolMode = undefined;
    this.showDrawCompleteTool = false;
    this.pendingDrawingToolsEnabled = false;
    this._stopTerraDraw();
  }

  showDeleteToolOverlay(isEnabled: boolean) {
    this.showDeleteTool = isEnabled;
    if (isEnabled) {
      this.showDrawToolbar = false;
    }
  }

  selectHandTool() {
    this.drawToolMode = 'hand';
    this.isDrawingToolsEnabled = false;
    this.showDrawCompleteTool = false;
    if (this.terraDraw) {
      this.terraDraw.clear();
      this.terraDraw.setMode('static');
    }
  }

  selectPolygonTool() {
    this.drawToolMode = 'polygon';
    this.isDrawingToolsEnabled = true;
    if (!this.terraDrawReady || !this.terraDraw) {
      this._ensureTerraDraw(() => {
        this.selectPolygonTool();
      });
      return;
    }
    this._activatePolygonDrawMode();
  }

  completeDraw() {
    if (!this.showDrawCompleteTool) {
      return;
    }
    this._finishTerraDrawPolygon();
  }

  deleteDraw() {
    this.showDeleteToolOverlay(false);
    if (this.config.onDrawDelete) {
      this.config.onDrawDelete();
    }
    this.startDrawing();
  }

  prepareForEditablePolygon() {
    this.showDrawToolbar = false;
    this.showDrawCompleteTool = false;
    this.isDrawingToolsEnabled = false;
    this.drawToolMode = undefined;
    this.pendingDrawingToolsEnabled = false;
    this._unregisterTerraDrawFinishListeners();
    if (this.terraDraw) {
      this.terraDraw.clear();
      this.terraDraw.setMode('static');
    }
  }

  finalizeAfterEditablePolygonMounted() {
    this._stopTerraDraw();
    this.mapPolygonDrawService.removeTerraDrawLeftovers(this.mapInstance, this.config.$timeout);
    this.showDeleteToolOverlay(true);
  }

  private _ensureTerraDraw(onReady?: () => void) {
    if (this.terraDrawReady && this.terraDraw) {
      if (onReady) {
        onReady();
      }
      return;
    }
    if (!this.mapInstance || this.terraDraw) {
      return;
    }

    this.mapPolygonDrawService.initTerraDraw(this.mapInstance, (draw) => {
      this.terraDraw = draw;
      this.terraDrawReady = true;
      this.terraDraw.on('finish', this.terraDrawFinishHandler);
      this.terraDraw.on('change', this.terraDrawChangeHandler);
      if (onReady) {
        onReady();
      } else if (this.pendingDrawingToolsEnabled) {
        this.startDrawing();
      }
    });
  }

  private _stopTerraDraw() {
    this._unregisterTerraDrawFinishListeners();
    if (this.terraDraw) {
      if (this.terraDrawFinishHandler) {
        this.terraDraw.off('finish', this.terraDrawFinishHandler);
      }
      if (this.terraDrawChangeHandler) {
        this.terraDraw.off('change', this.terraDrawChangeHandler);
      }
      this.mapPolygonDrawService.destroyTerraDraw(this.terraDraw);
      this.terraDraw = undefined;
      this.terraDrawReady = false;
    }
    this.mapPolygonDrawService.removeTerraDrawLeftovers(this.mapInstance, this.config.$timeout);
  }

  private _activatePolygonDrawMode() {
    this.pendingDrawingToolsEnabled = false;
    this.terraDraw.clear();
    this.terraDraw.setMode('polygon');
    this._focusTerraDrawMap();
    this._registerTerraDrawFinishListeners();
    this._syncDrawCompleteToolVisibility();
    this.config.$timeout(0).then(() => {
      if (!this.terraDraw || !this.isDrawingToolsEnabled || this.drawToolMode !== 'polygon') {
        return;
      }
      if (this.terraDraw.getMode() !== 'polygon') {
        this.terraDraw.setMode('polygon');
      }
      this._focusTerraDrawMap();
      this._syncDrawCompleteToolVisibility();
    });
  }

  private _syncDrawCompleteToolVisibility() {
    this.showDrawCompleteTool = !!(this.showDrawToolbar
      && this.drawToolMode === 'polygon'
      && this.terraDraw
      && this.terraDraw.getMode() === 'polygon'
      && this.terraDraw.getModeState() === 'drawing');
  }

  private _registerTerraDrawFinishListeners() {
    if (!this.mapInstance || this.terraDrawContextMenuListener) {
      return;
    }
    const mapDiv = this.mapInstance.getDiv();
    this.terraDrawContextMenuListener = (event: Event) => {
      if (!this.showDrawToolbar || this.drawToolMode !== 'polygon' || !this.terraDraw) {
        return;
      }
      event.preventDefault();
      if (this.terraDraw.getMode() === 'polygon' && this.terraDraw.getModeState() === 'drawing') {
        this._finishTerraDrawPolygon();
      }
    };
    mapDiv.addEventListener('contextmenu', this.terraDrawContextMenuListener);
    this.terraDrawContextMenuElement = mapDiv;
  }

  private _unregisterTerraDrawFinishListeners() {
    if (this.terraDrawContextMenuListener && this.terraDrawContextMenuElement) {
      this.terraDrawContextMenuElement.removeEventListener('contextmenu', this.terraDrawContextMenuListener);
    }
    this.terraDrawContextMenuListener = undefined;
    this.terraDrawContextMenuElement = undefined;
  }

  private _focusTerraDrawMap() {
    if (!this.mapInstance) {
      return;
    }
    const mapDiv = this.mapInstance.getDiv();
    if (!mapDiv.getAttribute('tabindex')) {
      mapDiv.setAttribute('tabindex', '-1');
    }
    mapDiv.focus();
  }

  private _finishTerraDrawPolygon() {
    if (!this.terraDraw || this.terraDraw.getMode() !== 'polygon') {
      return;
    }
    const mapDiv = this.mapInstance.getDiv();
    mapDiv.dispatchEvent(new KeyboardEvent('keyup', {
      key: 'Enter',
      bubbles: true,
      cancelable: true
    }));
  }

  private _handleTerraDrawFinish(id) {
    if (!this.isDrawingToolsEnabled) {
      return;
    }

    const feature = this.terraDraw.getSnapshotFeature(id);
    if (!feature) {
      return;
    }

    const coordinates = this.mapPolygonDrawService.convertTerraDrawPolygonToCoordinates(feature);
    if (!this.mapPolygonDrawService.isValidPolygonRing(coordinates)) {
      this.terraDraw.clear();
      this.terraDraw.setMode('polygon');
      this._syncDrawCompleteToolVisibility();
      return;
    }

    this.prepareForEditablePolygon();
    this.config.onDrawFinish(coordinates);
  }
}

export class MapSelectionDrawSession {
  showDrawToolbar: boolean = false;
  showDeleteTool: boolean = false;
  showDrawCompleteTool: boolean = false;
  showRectangleTool: boolean = true;
  drawToolMode: string = 'hand';

  private mapInstance: google.maps.Map;
  private terraDraw: TerraDraw;
  private terraDrawReady: boolean;
  private isDrawingToolsEnabled: boolean;
  private terraDrawFinishHandler: (id: string | number) => void;
  private terraDrawChangeHandler: (ids: (string | number)[], type: string) => void;
  private terraDrawContextMenuListener: (event: Event) => void;
  private terraDrawContextMenuElement: HTMLElement;
  private touchScrollLockState: MapSelectionInteractionState = {};

  constructor(private mapPolygonDrawService: MapPolygonDrawService, private config: MapSelectionDrawSessionConfig) {
    this.terraDrawFinishHandler = (id) => {
      this._handleTerraDrawFinish(id);
    };
    this.terraDrawChangeHandler = () => {
      this._syncDrawCompleteToolVisibility();
    };
  }

  setMapInstance(mapInstance: google.maps.Map) {
    this.mapInstance = mapInstance;
    this._syncTouchScrollLock();
  }

  destroy() {
    this._syncTouchScrollLock(false);
    this._stopTerraDraw();
  }

  enable() {
    this.showDrawToolbar = true;
    this.drawToolMode = 'hand';
    this.isDrawingToolsEnabled = false;
    this._ensureTerraDraw(() => {
      this.selectHandTool();
    });
  }

  selectHandTool() {
    this.drawToolMode = 'hand';
    this.isDrawingToolsEnabled = false;
    this.showDrawCompleteTool = false;
    this._syncTouchScrollLock(false);
    if (this.terraDraw) {
      this.terraDraw.clear();
      this.terraDraw.setMode('static');
    }
  }

  selectRectangleTool() {
    this.drawToolMode = 'rectangle';
    this.isDrawingToolsEnabled = true;
    this.showDrawCompleteTool = false;
    this._syncTouchScrollLock(true);
    if (!this.terraDrawReady || !this.terraDraw) {
      this._ensureTerraDraw(() => {
        this.selectRectangleTool();
      });
      return;
    }
    this.terraDraw.clear();
    this.terraDraw.setMode('rectangle');
    this._focusTerraDrawMap();
  }

  selectPolygonTool() {
    this.drawToolMode = 'polygon';
    this.isDrawingToolsEnabled = true;
    this._syncTouchScrollLock(true);
    if (!this.terraDrawReady || !this.terraDraw) {
      this._ensureTerraDraw(() => {
        this.selectPolygonTool();
      });
      return;
    }
    this.terraDraw.clear();
    this.terraDraw.setMode('polygon');
    this._focusTerraDrawMap();
    this._registerTerraDrawFinishListeners();
    this._syncDrawCompleteToolVisibility();
  }

  completeDraw() {
    if (!this.showDrawCompleteTool) {
      return;
    }
    this._finishTerraDrawPolygon();
  }

  deleteDraw() {
    this.showDeleteTool = false;
    this.config.onDeleteCustomPolygons();
    this.showDrawToolbar = true;
    this.selectHandTool();
  }

  showDeleteToolOverlay(isEnabled: boolean) {
    this.showDeleteTool = isEnabled;
    if (isEnabled) {
      this.showDrawToolbar = false;
      this._syncTouchScrollLock(false);
    } else {
      this.showDrawToolbar = true;
    }
  }

  private _syncTouchScrollLock(enabled?: boolean) {
    const shouldLock = _.isUndefined(enabled)
      ? this.isDrawingToolsEnabled && (this.drawToolMode === 'rectangle' || this.drawToolMode === 'polygon')
      : enabled;
    setMapSelectionInteractionHandlers(this.mapInstance, this.touchScrollLockState, shouldLock);
  }

  private _ensureTerraDraw(onReady?: () => void) {
    if (this.terraDrawReady && this.terraDraw) {
      if (onReady) {
        onReady();
      }
      return;
    }
    if (!this.mapInstance || this.terraDraw) {
      return;
    }

    this.mapPolygonDrawService.initSelectionTerraDraw(this.mapInstance, (draw) => {
      this.terraDraw = draw;
      this.terraDrawReady = true;
      this.terraDraw.on('finish', this.terraDrawFinishHandler);
      this.terraDraw.on('change', this.terraDrawChangeHandler);
      if (onReady) {
        onReady();
      }
    });
  }

  private _stopTerraDraw() {
    this._unregisterTerraDrawFinishListeners();
    if (this.terraDraw) {
      this.terraDraw.off('finish', this.terraDrawFinishHandler);
      this.terraDraw.off('change', this.terraDrawChangeHandler);
      this.mapPolygonDrawService.destroyTerraDraw(this.terraDraw);
      this.terraDraw = undefined;
      this.terraDrawReady = false;
    }
    this.mapPolygonDrawService.removeTerraDrawLeftovers(this.mapInstance, this.config.$timeout);
  }

  private _syncDrawCompleteToolVisibility() {
    this.showDrawCompleteTool = !!(this.showDrawToolbar
      && this.drawToolMode === 'polygon'
      && this.terraDraw
      && this.terraDraw.getMode() === 'polygon'
      && this.terraDraw.getModeState() === 'drawing');
  }

  private _registerTerraDrawFinishListeners() {
    if (!this.mapInstance || this.terraDrawContextMenuListener) {
      return;
    }
    const mapDiv = this.mapInstance.getDiv();
    this.terraDrawContextMenuListener = (event: Event) => {
      if (!this.showDrawToolbar || this.drawToolMode !== 'polygon' || !this.terraDraw) {
        return;
      }
      event.preventDefault();
      if (this.terraDraw.getMode() === 'polygon' && this.terraDraw.getModeState() === 'drawing') {
        this._finishTerraDrawPolygon();
      }
    };
    mapDiv.addEventListener('contextmenu', this.terraDrawContextMenuListener);
    this.terraDrawContextMenuElement = mapDiv;
  }

  private _unregisterTerraDrawFinishListeners() {
    if (this.terraDrawContextMenuListener && this.terraDrawContextMenuElement) {
      this.terraDrawContextMenuElement.removeEventListener('contextmenu', this.terraDrawContextMenuListener);
    }
    this.terraDrawContextMenuListener = undefined;
    this.terraDrawContextMenuElement = undefined;
  }

  private _focusTerraDrawMap() {
    if (!this.mapInstance) {
      return;
    }
    const mapDiv = this.mapInstance.getDiv();
    if (!mapDiv.getAttribute('tabindex')) {
      mapDiv.setAttribute('tabindex', '-1');
    }
    mapDiv.focus();
  }

  private _finishTerraDrawPolygon() {
    if (!this.terraDraw || this.terraDraw.getMode() !== 'polygon') {
      return;
    }
    const mapDiv = this.mapInstance.getDiv();
    mapDiv.dispatchEvent(new KeyboardEvent('keyup', {
      key: 'Enter',
      bubbles: true,
      cancelable: true
    }));
  }

  private _handleTerraDrawFinish(id) {
    if (!this.isDrawingToolsEnabled) {
      return;
    }

    const feature = this.terraDraw.getSnapshotFeature(id);
    if (!feature) {
      return;
    }

    if (this.drawToolMode === 'rectangle') {
      const bounds = this.mapPolygonDrawService.convertTerraDrawFeatureToBounds(feature);
      if (!bounds) {
        this.terraDraw.clear();
        this.terraDraw.setMode('rectangle');
        return;
      }
      this.terraDraw.clear();
      this.terraDraw.setMode('rectangle');
      this._focusTerraDrawMap();
      this.config.onRectangleComplete(bounds);
      return;
    }

    const coordinates = this.mapPolygonDrawService.convertTerraDrawPolygonToCoordinates(feature);
    if (!this.mapPolygonDrawService.isValidPolygonRing(coordinates)) {
      this.terraDraw.clear();
      this.terraDraw.setMode('polygon');
      this._syncDrawCompleteToolVisibility();
      return;
    }

    const polygon = this.mapPolygonDrawService.createGoogleMapsPolygon(this.mapInstance, coordinates, this.config.polygonOptions);
    this.terraDraw.clear();
    this.terraDraw.setMode('static');
    this.drawToolMode = 'hand';
    this.isDrawingToolsEnabled = false;
    this._syncTouchScrollLock(false);
    this._unregisterTerraDrawFinishListeners();
    this.mapPolygonDrawService.removeTerraDrawLeftovers(this.mapInstance, this.config.$timeout);
    this.config.onPolygonComplete(polygon);
  }
}

export interface MapRectangleSelectSessionConfig {
  onComplete: (bounds: google.maps.LatLngBounds) => void;
  onModeChange?: (mode: 'pan' | 'rectangle') => void;
}

export class MapRectangleSelectSession {
  private mapInstance: google.maps.Map;
  private mode: 'pan' | 'rectangle' = 'pan';
  private rectangle: google.maps.Rectangle;
  private dragStart: google.maps.LatLng;
  private mouseDownListener: google.maps.MapsEventListener;
  private moveListener: google.maps.MapsEventListener;
  private upListener: google.maps.MapsEventListener;
  private documentUpListener: () => void;
  private documentTouchStartListener: (event: TouchEvent) => void;
  private isDrawing: boolean;
  private skipNextClick: boolean;
  private touchScrollLockState: MapSelectionInteractionState = {};

  constructor(private config: MapRectangleSelectSessionConfig) {
  }

  setMapInstance(map: google.maps.Map) {
    this.mapInstance = map;
    if (!this.mouseDownListener) {
      this.mouseDownListener = map.addListener('mousedown', (event) => {
        this.handleMouseDown(event);
      });
    }
    this._syncTouchScrollLock(this.mode === 'rectangle');
  }

  destroy() {
    this._syncTouchScrollLock(false);
    this._clearDragListeners();
    if (this.rectangle) {
      this.rectangle.setMap(null);
      this.rectangle = undefined;
    }
    if (this.mouseDownListener) {
      google.maps.event.removeListener(this.mouseDownListener);
      this.mouseDownListener = undefined;
    }
  }

  isRectangleActive(): boolean {
    return this.mode === 'rectangle';
  }

  isDrawingRectangle(): boolean {
    return !!this.isDrawing;
  }

  consumeSkipNextClick(): boolean {
    if (this.skipNextClick) {
      this.skipNextClick = false;
      return true;
    }
    return false;
  }

  setMode(mode: 'pan' | 'rectangle') {
    this.mode = mode;
    this._applyMapModeOptions();
    this._syncTouchScrollLock(mode === 'rectangle');
    if (this.config.onModeChange) {
      this.config.onModeChange(mode);
    }
  }

  private _syncTouchScrollLock(enabled: boolean) {
    setMapSelectionInteractionHandlers(this.mapInstance, this.touchScrollLockState, enabled);
  }

  handleMouseDown(event: google.maps.MapMouseEvent) {
    if (this.mode !== 'rectangle' || !event || !event.latLng || this.isDrawing || !this.mapInstance) {
      return;
    }

    this._applyMapModeOptions();
    this.isDrawing = true;
    this.dragStart = event.latLng;
    this.rectangle = new google.maps.Rectangle({
      map: this.mapInstance,
      bounds: new google.maps.LatLngBounds(this.dragStart, this.dragStart),
      fillColor: '#676767',
      fillOpacity: 0.4,
      strokeWeight: 2,
      strokeColor: '#6060FB',
      clickable: false
    });

    this.moveListener = this.mapInstance.addListener('mousemove', (moveEvent) => {
      this.handleMouseMove(moveEvent.latLng);
    });

    this.upListener = this.mapInstance.addListener('mouseup', () => {
      this.handleMouseUp();
    });

    // The map fires 'mouseup' only when released over the map itself. A release
    // outside the map, over a data-layer marker, or an interrupted touch would
    // otherwise leave the drag stuck (rectangle never removed, isDrawing true).
    this.documentUpListener = () => {
      this.handleMouseUp();
    };
    document.addEventListener('mouseup', this.documentUpListener);
    document.addEventListener('touchend', this.documentUpListener);
    document.addEventListener('touchcancel', this.documentUpListener);
    document.addEventListener('pointercancel', this.documentUpListener);

    // A second finger means the user is pinch-zooming, not selecting: discard
    // the rectangle started by the first finger and let the map handle the gesture.
    this.documentTouchStartListener = (touchEvent: TouchEvent) => {
      if (touchEvent.touches.length >= 2) {
        this._cancelDrawing();
      }
    };
    document.addEventListener('touchstart', this.documentTouchStartListener);
  }

  handleMouseMove(currentLatLng: google.maps.LatLng) {
    if (!this.isDrawing || !this.rectangle || !this.dragStart || !currentLatLng) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(this.dragStart);
    bounds.extend(currentLatLng);
    this.rectangle.setBounds(bounds);
  }

  handleMouseUp() {
    this._clearDragListeners();
    if (this.mode === 'rectangle') {
      this._applyMapModeOptions();
    }

    if (!this.isDrawing) {
      return;
    }

    this.isDrawing = false;
    this.skipNextClick = true;

    const rectangle = this.rectangle;
    this.rectangle = undefined;
    this.dragStart = undefined;

    if (!rectangle) {
      return;
    }

    const bounds = rectangle.getBounds();
    rectangle.setMap(null);

    if (!bounds) {
      return;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    if (ne.equals(sw)) {
      return;
    }

    this.config.onComplete(bounds);
  }

  private _cancelDrawing() {
    this._clearDragListeners();
    this.isDrawing = false;
    this.dragStart = undefined;
    if (this.rectangle) {
      this.rectangle.setMap(null);
      this.rectangle = undefined;
    }
  }

  private _clearDragListeners() {
    if (this.moveListener) {
      google.maps.event.removeListener(this.moveListener);
      this.moveListener = undefined;
    }
    if (this.upListener) {
      google.maps.event.removeListener(this.upListener);
      this.upListener = undefined;
    }
    if (this.documentUpListener) {
      document.removeEventListener('mouseup', this.documentUpListener);
      document.removeEventListener('touchend', this.documentUpListener);
      document.removeEventListener('touchcancel', this.documentUpListener);
      document.removeEventListener('pointercancel', this.documentUpListener);
      this.documentUpListener = undefined;
    }
    if (this.documentTouchStartListener) {
      document.removeEventListener('touchstart', this.documentTouchStartListener);
      this.documentTouchStartListener = undefined;
    }
  }

  // TerraDraw's adapter re-enables map dragging on every pointerup, so rectangle
  // mode options must be re-applied whenever a new drag starts.
  private _applyMapModeOptions() {
    if (!this.mapInstance) {
      return;
    }
    const isPan = this.mode === 'pan';
    this.mapInstance.setOptions({
      draggable: isPan,
      draggableCursor: isPan ? null : 'crosshair',
      scrollwheel: true
    });
  }
}

export class MapPolygonDrawService {

  /* @ngInject */
  constructor() {
  }

  createSession(config: MapPolygonDrawSessionConfig): MapPolygonDrawSession {
    return new MapPolygonDrawSession(this, config);
  }

  createSelectionSession(config: MapSelectionDrawSessionConfig): MapSelectionDrawSession {
    return new MapSelectionDrawSession(this, config);
  }

  createRectangleSelectSession(config: MapRectangleSelectSessionConfig): MapRectangleSelectSession {
    return new MapRectangleSelectSession(config);
  }

  initTerraDraw(map: google.maps.Map, onReady: (draw: TerraDraw) => void) {
    this._initTerraDraw(map, onReady, false);
  }

  initSelectionTerraDraw(map: google.maps.Map, onReady: (draw: TerraDraw) => void) {
    this._initTerraDraw(map, onReady, true);
  }

  convertTerraDrawFeatureToBounds(feature) {
    const coordinates = this.convertTerraDrawPolygonToCoordinates(feature);
    if (!coordinates.length) {
      return undefined;
    }
    let minLat = coordinates[0][1];
    let maxLat = coordinates[0][1];
    let minLng = coordinates[0][0];
    let maxLng = coordinates[0][0];
    _.forEach(coordinates, (coordinate) => {
      minLat = Math.min(minLat, coordinate[1]);
      maxLat = Math.max(maxLat, coordinate[1]);
      minLng = Math.min(minLng, coordinate[0]);
      maxLng = Math.max(maxLng, coordinate[0]);
    });
    return new google.maps.LatLngBounds(
      {lat: minLat, lng: minLng},
      {lat: maxLat, lng: maxLng}
    );
  }

  createGoogleMapsPolygon(map: google.maps.Map, coordinates: number[][], options: google.maps.PolygonOptions) {
    let ring = angular.copy(coordinates);
    if (ring.length > 1) {
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] === last[0] && first[1] === last[1]) {
        ring = ring.slice(0, -1);
      }
    }
    return new google.maps.Polygon({
      paths: _.map(ring, (coordinate) => {
        return {lat: coordinate[1], lng: coordinate[0]};
      }),
      map: map,
      ...options
    });
  }

  private _getTerraDrawShapeStyles() {
    return {
      fillColor: '#676767',
      fillOpacity: 0.4,
      outlineColor: '#6060FB',
      outlineWidth: 2,
      outlineOpacity: 1,
      lineStringColor: '#6060FB',
      lineStringWidth: 2,
      lineStringOpacity: 1,
      coordinatePointColor: '#6060FB',
      coordinatePointWidth: 7,
      coordinatePointOutlineColor: '#ffffff',
      coordinatePointOutlineWidth: 2,
      closingPointColor: '#6060FB',
      closingPointWidth: 9,
      closingPointOutlineColor: '#ffffff',
      closingPointOutlineWidth: 2
    } as any;
  }

  private _initTerraDraw(map: google.maps.Map, onReady: (draw: TerraDraw) => void, includeRectangle: boolean) {
    const mapDiv = map.getDiv();
    if (!mapDiv.id) {
      mapDiv.id = includeRectangle ? 'map-selection-draw-terra' : 'map-polygon-draw-terra';
    }

    const createDrawInstance = () => {
      const shapeStyles = this._getTerraDrawShapeStyles();
      const modes: any[] = [
        new TerraDrawPolygonMode({
          pointerDistance: 20,
          showCoordinatePoints: true,
          pointerEvents: {
            leftClick: true,
            rightClick: false,
            contextMenu: false,
            onDragStart: false,
            onDrag: false,
            onDragEnd: false
          },
          styles: includeRectangle ? shapeStyles : {
            ...shapeStyles,
            fillOpacity: 0
          }
        })
      ];
      if (includeRectangle) {
        modes.push(new TerraDrawRectangleMode({
          styles: shapeStyles
        }));
      }
      modes.push(new TerraDrawRenderMode({
        modeName: 'static',
        styles: {}
      }));

      const draw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          lib: google.maps,
          map,
          forwardMapElementEvents: true,
          isolatedData: true
        }),
        modes: modes
      });

      draw.on('ready', () => {
        draw.setMode('static');
        onReady(draw);
      });
      draw.start();
    };

    if (map.getProjection()) {
      createDrawInstance();
    } else {
      google.maps.event.addListenerOnce(map, 'projection_changed', createDrawInstance);
    }
  }

  convertTerraDrawPolygonToCoordinates(feature) {
    const geometryType = _.get(feature, 'geometry.type');
    let ring;
    if (geometryType === 'LineString') {
      ring = _.get(feature, 'geometry.coordinates', []);
    } else {
      ring = _.get(feature, 'geometry.coordinates[0]', []);
    }
    if (!ring.length) {
      return [];
    }
    const coordinates = angular.copy(ring);
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      coordinates.push(first);
    }
    return coordinates;
  }

  destroyTerraDraw(draw: TerraDraw) {
    if (draw && draw.enabled) {
      draw.stop();
    }
  }

  isValidPolygonRing(ring?) {
    return _.isArray(ring) && ring.length >= 3;
  }

  removeTerraDrawLeftovers(mapInstance: google.maps.Map, $timeout: ITimeoutService) {
    this._removeTerraDrawLeftovers(mapInstance);
    $timeout(100).then(() => this._removeTerraDrawLeftovers(mapInstance));
  }

  extractPolygonCoordinates(polygon: google.maps.Polygon) {
    const path = polygon.getPath();
    const coordinates = _.map(path.getArray(), (coords) => {
      return [coords.lng(), coords.lat()];
    });
    coordinates.push(coordinates[0]);
    return coordinates;
  }

  buildEditablePolygonEvents($timeout: ITimeoutService, onTogglePolygonEditMode: () => void, onCoordinatesChange: (coordinates: number[][]) => void) {
    return {
      rightclick: (gObject, eventName, model, eventArgs) => {
        const mapEvent = eventArgs && eventArgs.length ? eventArgs[0] : eventArgs;
        const vertex = mapEvent && _.isNumber(mapEvent.vertex) ? mapEvent.vertex : undefined;

        if (!_.isUndefined(vertex)) {
          const path = gObject.getPath();
          path.removeAt(vertex);
        } else {
          onTogglePolygonEditMode();
        }
      },
      dragend: (gObject) => {
        onCoordinatesChange(this.extractPolygonCoordinates(gObject));
      }
    };
  }

  private _removeTerraDrawLeftovers(mapInstance: google.maps.Map) {
    if (!mapInstance) {
      return;
    }
    const dataLayer = mapInstance.data;
    const leftovers = [];
    dataLayer.forEach((feature) => {
      if (feature.getProperty('mode')) {
        leftovers.push(feature);
      }
    });
    _.forEach(leftovers, (feature) => {
      dataLayer.remove(feature);
    });
  }
}

angular
  .module('bringoz.admin.map')
  .service('mapPolygonDrawService', MapPolygonDrawService);
